/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import {Level, Metric, MetricsState, Record, User} from '@exc/metrics';

const OVERSIZE_MSG = 'Oversized metric';

/**
 * Apply special handling where needed to convert objects to a simple
 * property bag.
 *
 * @param {any} o The object to be converted.
 * @returns {any} A simple bag or the original object if no conversion was
 * needed.
 */
function makePropertyBag(o: any): any {
  if (o instanceof Error) {
    const e = o as any;
    return {
      columnNumber: e.columnNumber,
      fileName: e.fileName,
      lineNumber: e.lineNumber,
      message: e.message,
      name: e.name,
      stack: e.stack
    };
  } else {
    return o;
  }
}

function next(): void {
  Payload.body.log = [];
  Payload.body.user = [];
  ++Payload.body.metricsState.payloadSequence;
  Payload.size = 0;
}

function finalizeRecord(record: Record, state: MetricsState): void {
  if (Array.isArray(record.data)) {
    record.data = record.data.map(makePropertyBag);
  } else if (record.data) {
    record.data = makePropertyBag(record.data);
  } else {
    delete record.data;
  }

  const a = (global as any).adobeMetrics;
  record.metricsState = Object.assign({}, state, {
    guid: (global as any).adobeMetrics.guid,
    timeOrigin: (global as any).adobeMetrics.timeOrigin
  });

  const l = (global as any).location;
  const d = (global as any).document;
  record.metricsState.window = Object.assign({}, a.metricsState && a.metricsState.window,
    d ? {visibility: d.visibilityState} : undefined,
    l ? {location: {href: l.href, origin: l.origin}} : undefined
  );

  if ((global as any).adobeMetrics.runtime.options.timestamp) {
    record.timestamp = record.dateNow ? new Date(record.dateNow) : new Date();
  }
}

function count(): number {
  return Payload.body.user.length + Payload.body.log.length;
}

function countUser(): number {
  return Payload.body.user.length;
}

function addUser(user: User, state: MetricsState): void {
  finalizeRecord(user, state);
  const s = JSON.stringify(user);
  Payload.body.user.push(s);
  Payload.size += s.length;
}

function checkMetricSize(metric: Metric): string {
  let s = JSON.stringify(metric);
  if (s.length > Payload.oversizeLimit) {
    delete metric.data;
    metric.data = makePropertyBag(new Error(OVERSIZE_MSG));
    metric.data.payloadSize = s.length;
    metric.level = Level.ERROR;
    metric.message = OVERSIZE_MSG + (!metric.message ? '' : ' | Original message: ' + metric.message);
    s = JSON.stringify(metric);
  }
  return s;
}

function addMetric(metric: Metric, state: MetricsState): void {
  const d = (global as any).document;
  if (d?.visibilityState && d.visibilityState === 'hidden') {
    if (metric.recordType === 'ResourceTiming') {
      return;
    }
  }
  finalizeRecord(metric, state);
  metric.offset = (global as any).adobeMetrics.now() -
      (metric.metricsState && metric.metricsState.pageState && metric.metricsState.pageState.epoch || 0);
  const s = checkMetricSize(metric);
  Payload.body.log.push(s);
  Payload.size += s.length;
}

// Append the userCount and logCount records to Log table
function appendPayloadCount(reason: string): void {
  const a = (global as any).adobeMetrics;
  const metric = {
    counter: Payload.body.log.length + Payload.body.user.length,
    data: {log: Payload.body.log.length, reason, user: Payload.body.user.length},
    dateNow: Date.now(),
    recordType: 'PayloadCount',
    user: {}
  };
  if (a.user && a.user.groupId && a.user.sessionId && a.user.userId) {
    Object.assign(metric, {user: {groupId: a.user.groupId, sessionId: a.user.sessionId, userId: a.user.userId}});
  }
  finalizeRecord(metric, {sdkVersion: (global as any).adobeMetrics.runtime.version} as any);
  const s = JSON.stringify(metric);
  Payload.body.log.push(s);
  Payload.size += s.length;
}

function toJSONString(): string {
  Object.assign(Payload.body.metricsState,
    {runtimeVersion: (global as any).adobeMetrics.runtime.version}, (global as any).adobeMetrics.metricsState);
  return '{"log":[' + Payload.body.log.join(',')
    + '],"metricsState":' + JSON.stringify(Payload.body.metricsState)
    + ',"user":[' + Payload.body.user.join(',') + ']}';
}

const log: string[] = [];
const user: string[] = [];

const Payload = {
  addMetric,
  addUser,
  appendPayloadCount,
  body: {
    log,
    metricsState: {payloadSequence: 1},
    user
  },
  count,
  countUser,
  next,
  // Gateway limit 3 MB - max flush threshold 500kB - 1 byte
  // This way we never flush a payload > 3MB
  oversizeLimit: 2499999,
  size: 0,
  toJSONString
};
export default Payload;
